import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import colors from 'common/styles/colors';
import useOrgWidgetData from '../hooks/useOrgWidgetData';
import OrgWidgetFrame from './OrgWidgetFrame';
import { OrgWidgetDataState } from './OrgWidgetStates';
import { getOrgWidgetTitle, useResolveOrgWidgetMeta } from './orgWidgetMeta';

const WIDGET_NAME = 'org_device_health_rollup';

const List = styled.div`
  padding: 16px 20px;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const Dot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${props => props.color};
  flex-shrink: 0;
`;

const Label = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #374151;
`;

const Value = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
  flex-shrink: 0;
`;

const DOT_COLORS = {
  reachable: colors.normal,
  low_battery: colors.warning,
  wifi_issues: colors.slight,
  firmware_outdated: colors.moderate,
  missed_measurements: colors.critical
};

const OrgDeviceHealthRollup = ({ organisationId, widgetsMetadata, title }) => {
  const { t } = useTranslation();
  const meta = useResolveOrgWidgetMeta(widgetsMetadata, WIDGET_NAME);
  const widgetTitle = getOrgWidgetTitle(title, meta);
  const { data, loading, error, reload } = useOrgWidgetData(organisationId, meta);
  // Null-coalesce before mapping: JSX children evaluate eagerly during render,
  // so `data.items.map` would run (and throw) while data is still null — the
  // OrgWidgetDataState isEmpty guard can't prevent that. Seen when a widget is
  // deleted then re-added and mounts fresh with data === null.
  const items = (data && data.items) || [];

  return (
    <OrgWidgetDataState
      title={widgetTitle}
      loading={loading}
      hasError={error}
      onRetry={reload}
      isEmpty={!items.length}
      emptyMessage={t('orgView.deviceRollup.noData', 'No device health data')}
      emptyScrollable={false}
    >
      <OrgWidgetFrame title={widgetTitle}>
        <List>
          {items.map(item => (
            <Row key={item.key}>
              <Left>
                <Dot color={DOT_COLORS[item.key] || colors.greyD} />
                <Label>{item.label}</Label>
              </Left>
              <Value>{item.display}</Value>
            </Row>
          ))}
        </List>
      </OrgWidgetFrame>
    </OrgWidgetDataState>
  );
};

OrgDeviceHealthRollup.propTypes = {
  organisationId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  widgetsMetadata: PropTypes.object,
  title: PropTypes.string
};

OrgDeviceHealthRollup.defaultProps = {
  widgetsMetadata: null,
  title: null
};

export default OrgDeviceHealthRollup;
